import React, { useEffect, useMemo, useState } from 'react';
import {
  IconAlertTriangle,
  IconCircleCheck,
  IconClock,
  IconDoor,
  IconHelpCircle,
  IconLoader2,
  IconPlayerPlay,
  IconTool,
} from '@tabler/icons-react';
import {
  TimelineMachine,
  TimelineSegment,
  TimelineStatusCategory,
  currentSegmentForRow,
  effectiveRemainingSeconds,
  formatCycleLabel,
  formatDuration,
  formatRemainingClock,
  formatTimelineErrorDetail,
  formatTimelineStatus,
  getTimelineStatusCategory,
  machineKey,
  toMs,
} from './statusTimelineUtils';

const CATEGORY_ICON: Record<TimelineStatusCategory, React.ComponentType<any>> = {
  available: IconCircleCheck,
  ready: IconPlayerPlay,
  'in-use': IconLoader2,
  complete: IconClock,
  error: IconAlertTriangle,
  'out-of-order': IconTool,
  unknown: IconHelpCircle,
};

export type MachineStateSnapshot = {
  row: TimelineMachine;
  category: TimelineStatusCategory;
  label: string;
  sinceMs: number | null;
  errorDetail: string | null;
};

export function machineSnapshot(
  row: TimelineMachine,
  segments: TimelineSegment[]
): MachineStateSnapshot {
  const seg = currentSegmentForRow(row, segments);
  const category = seg ? getTimelineStatusCategory(seg.statusName) : 'unknown';
  return {
    row,
    category,
    label: seg ? formatTimelineStatus(seg.statusName) : 'No recent data',
    sinceMs: seg ? toMs(seg.start_time) : null,
    errorDetail: seg && category === 'error' ? formatTimelineErrorDetail(seg) : null,
  };
}

type Props = {
  machines: TimelineMachine[];
  segments: TimelineSegment[];
  onSelect?: (row: TimelineMachine, x: number, y: number) => void;
};

/** What each machine is doing right now: the view that answers floor questions. */
const StatusMachineGrid: React.FC<Props> = ({ machines, segments, onSelect }) => {
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const snapshots = useMemo(
    () => machines.map((row) => machineSnapshot(row, segments)),
    [machines, segments]
  );

  return (
    <ul className="sq-machines">
      {snapshots.map(({ row, category, label, sinceMs, errorDetail }) => {
        const Icon = CATEGORY_ICON[category];
        const remaining = effectiveRemainingSeconds(row, nowMs);
        const cycle = formatCycleLabel(row);
        const showCycle = category === 'in-use' || category === 'ready';
        return (
          <li key={machineKey(row.mac_id, row.name)}>
            <button
              type="button"
              className={`sq-machine cat-${category}`}
              onClick={(e) => onSelect?.(row, e.clientX, e.clientY)}
              onContextMenu={(e) => {
                if (!onSelect) return;
                e.preventDefault();
                onSelect(row, e.clientX, e.clientY);
              }}
              aria-haspopup={onSelect ? 'menu' : undefined}
              aria-label={`${row.name}, ${label}${
                remaining != null && remaining > 0
                  ? `, ${formatRemainingClock(remaining)} remaining`
                  : ''
              }. Show actions`}
            >
              <span className="sq-machine-top">
                <span className="sq-machine-name">{row.name}</span>
                {remaining != null && remaining > 0 ? (
                  <span className="sq-machine-clock">{formatRemainingClock(remaining)}</span>
                ) : null}
              </span>
              <span className="sq-machine-status">
                <Icon size={15} stroke={2} aria-hidden="true" />
                {label}
              </span>
              <span className="sq-machine-meta">
                {errorDetail ||
                  (showCycle && cycle) ||
                  (sinceMs != null ? `for ${formatDuration(nowMs - sinceMs)}` : ' ')}
              </span>
              {row.is_door_open === true ? (
                <span className="sq-machine-door">
                  <IconDoor size={13} aria-hidden="true" />
                  Door open
                </span>
              ) : null}
            </button>
          </li>
        );
      })}
    </ul>
  );
};

export default StatusMachineGrid;
